/**
 * OFCard - Componente de Card para Ordem de Fabrico
 * Extraído de GestaoOFs.js para melhor modularidade
 */
import React from 'react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import {
  CheckCircle, AlertTriangle, Zap, Layers, Box, Wrench,
  Package, Activity, PieChart, Edit, ArrowRight, Trash2, Settings2, Settings, Download,
  CheckSquare, Square
} from 'lucide-react';

const STATUS_CONFIG = {
  rascunho: { label: 'Rascunho', color: 'bg-stone-100 text-stone-600' },
  pendente: { label: 'Ready to Start', color: 'bg-blue-100 text-blue-600' },
  aguardar_entrada: { label: 'Aguardar Entrada', color: 'bg-amber-100 text-amber-600' },
  em_curso: { label: 'Em Curso', color: 'bg-green-100 text-green-600' },
  pausado: { label: 'Pausado', color: 'bg-orange-100 text-orange-600' },
  terminado: { label: 'Terminado', color: 'bg-emerald-100 text-emerald-600' },
  cancelado: { label: 'Cancelado', color: 'bg-red-100 text-red-600' },
};

export default function OFCard({
  of,
  anomalias,
  categorias = [],
  onOpenLotes,
  onOpenAcompanhar,
  onOpenAnaliseTempos,
  onOpenEdit,
  onOpenOperacoes,
  onOpenStatus,
  onDelete,
  onExportCsv,
  getStatusBadge,
  selectionMode,
  isSelected,
  onToggleSelect,
}) {
  // Resolver nomes das categorias especiais
  const catsEspeciais = (of.categorias_especiais || []).map(catId => {
    const cat = categorias.find(c => c.id === catId);
    return cat ? cat.codigo || cat.nome : null;
  }).filter(Boolean);

  return (
    <Card 
      className={`p-6 hover:shadow-lg transition-shadow ${of.urgente ? 'border-2 border-red-500 bg-red-50/30' : ''} ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50/30' : ''}`}
      data-testid={`of-card-${of.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3">
          {selectionMode && (
            <button
              onClick={(e) => { e.stopPropagation(); onToggleSelect && onToggleSelect(of.id); }}
              className="mt-1 flex-shrink-0"
              data-testid={`checkbox-of-${of.id}`}
            >
              {isSelected
                ? <CheckSquare className="h-6 w-6 text-blue-600" />
                : <Square className="h-6 w-6 text-stone-400 hover:text-blue-500" />}
            </button>
          )}
          <div>
          <p className="text-xs font-mono text-stone-500 mb-1">OF</p>
          <div className="flex items-center gap-2">
            <h3 className="text-2xl font-heading font-bold text-stone-900">{of.numero_of}</h3>
            {of.urgente && (
              <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-red-500 text-white text-xs font-medium">
                <Zap className="h-3 w-3" />
                Urgente
              </span>
            )}
            {catsEspeciais.map((nome, idx) => (
              <span key={idx} className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold" style={{ background: '#DBEAFE', color: '#1D4ED8' }} data-testid={`badge-cat-especial-${nome}`}>
                <Layers className="h-3 w-3" />
                {nome}
              </span>
            ))}
          </div>
        </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <button
            onClick={() => onOpenStatus(of)}
            className="hover:scale-105 transition-transform"
            title="Clique para alterar estado"
          >
            {getStatusBadge(of.status)}
          </button>
          {anomalias?.abertas > 0 && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              {anomalias.abertas} anomalia{anomalias.abertas > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
      
      <div className="space-y-3">
        {/* Cliente e Artigo */}
        <div>
          <p className="text-xl font-bold text-stone-900">{of.cliente}</p>
          <p className="text-base font-medium text-stone-600">{of.artigo}</p>
        </div>

        {/* Datas e Observações */}
        {(of.previsao_entrada || of.data_entrega || of.observacoes) && (
          <div className="space-y-1.5">
            {(of.previsao_entrada || of.data_entrega) && (
              <div className="flex items-center gap-4 text-base">
                {of.previsao_entrada && (() => {
                  const d = new Date(of.previsao_entrada);
                  const hoje = new Date(); hoje.setHours(0,0,0,0);
                  const isVencida = d <= hoje;
                  return (
                    <span className="text-stone-500">Prev. Entrada: <span className={`font-semibold ${isVencida ? 'text-red-600 animate-pulse' : 'text-stone-700'}`}>{d.toLocaleDateString('pt-PT')}</span></span>
                  );
                })()}
                {of.data_entrega && (
                  <span className="text-stone-500">Data Entrega: <span className="font-semibold text-stone-700">{new Date(of.data_entrega).toLocaleDateString('pt-PT')}</span></span>
                )}
              </div>
            )}
            {of.observacoes && (
              <p className="px-3 py-2 rounded bg-amber-50 border border-amber-200 text-amber-800 italic text-base" title={of.observacoes}>{of.observacoes}</p>
            )}
          </div>
        )}
        
        {/* Itens a Aguardar */}
        {of.aguardar_items && of.aguardar_items.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            {of.aguardar_items.map((item, idx) => (
              <span
                key={idx}
                className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${
                  item.recebido 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-amber-100 text-amber-700'
                }`}
                title={item.data_prevista ? `Previsto: ${item.data_prevista}` : ''}
              >
                {item.tipo === 'estrutura' && <Layers className="h-3 w-3" />}
                {item.tipo === 'pecas' && <Box className="h-3 w-3" />}
                {item.tipo === 'acessorios' && <Wrench className="h-3 w-3" />}
                {item.tipo.charAt(0).toUpperCase() + item.tipo.slice(1)}
                {item.recebido ? <CheckCircle className="h-3 w-3" /> : item.data_prevista && <span className="text-[10px] ml-1">({item.data_prevista})</span>}
              </span>
            ))}
          </div>
        )}
        
        {/* Quantidade e Lotes */}
        <div className="flex items-center gap-4 py-3 px-4 bg-stone-50 rounded-lg">
          <div className="flex-1">
            <p className="text-sm text-stone-500">Quantidade</p>
            <p className="text-2xl font-bold text-stone-900">
              {of.quantidade_total} <span className="text-base font-normal text-stone-500">un.</span>
            </p>
          </div>
          <button 
            onClick={() => onOpenLotes(of)}
            className="text-center px-4 border-l border-stone-200 hover:bg-primary/10 rounded cursor-pointer transition-colors"
            title="Gerir Lotes"
            data-testid={`btn-lotes-card-${of.id}`}
          >
            <p className="text-sm text-stone-500">Lotes</p>
            {(!of.lotes || of.lotes.length === 0) ? (
              <div className="animate-pulse">
                <p className="text-lg font-bold text-red-600">Sem Lotes</p>
                <p className="text-[10px] text-red-500">Clique para criar</p>
              </div>
            ) : (
              <>
                <p className="text-2xl font-bold text-primary">{of.lotes.length}</p>
                <p className="text-xs text-stone-500">
                  {of.lotes.reduce((acc, l) => acc + (l.quantidade || 0), 0)} un.
                </p>
                {of.lotes.some(l => l.status === 'pendente') && (
                  <span className="text-[10px] text-amber-600">
                    {of.lotes.filter(l => l.status === 'pendente').length} pendente(s)
                  </span>
                )}
              </>
            )}
          </button>
          <div className="text-center px-4 border-l border-stone-200">
            <p className="text-sm text-stone-500">Operações</p>
            <p className="text-2xl font-bold text-stone-700">{of.operacoes?.length || 0}</p>
          </div>
        </div>
        
        {/* Botões Lotes e Operações */}
        <div className="grid grid-cols-2 gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            className="text-primary border-primary/30 hover:bg-primary/5"
            onClick={() => onOpenLotes(of)} 
            data-testid={`btn-gerir-lotes-${of.id}`}
          >
            <Package className="h-4 w-4 mr-2" />
            Lotes
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => onOpenOperacoes && onOpenOperacoes(of)} 
            data-testid={`btn-operacoes-card-${of.id}`}
            className="text-purple-600 border-purple-200 hover:bg-purple-50"
          >
            <Settings className="h-4 w-4 mr-2" />
            Operações
          </Button>
        </div>

        {/* Ações */}
        <div className="flex flex-wrap gap-2 pt-3 border-t border-stone-100">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => onOpenAcompanhar(of)} 
            data-testid={`btn-acompanhar-${of.id}`}
          >
            <Activity className="h-4 w-4 mr-1" /> Acompanhar
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => onOpenAnaliseTempos(of)} 
            data-testid={`btn-analise-${of.id}`}
            title="Análise de Tempos"
            className="text-blue-600 hover:bg-blue-50"
          >
            <PieChart className="h-4 w-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => onOpenEdit(of)} 
            data-testid={`btn-editar-${of.id}`}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => onOpenStatus(of)} 
            data-testid={`btn-status-${of.id}`} 
            title="Alterar estado"
          >
            <ArrowRight className="h-4 w-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => onDelete(of)} 
            className="text-red-500 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
            title="Eliminar OF"
            data-testid={`btn-delete-${of.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      {/* Data de criação - discreto no canto inferior direito */}
      {of.criado_em && (
        <div className="flex justify-end mt-2">
          <span className="text-[10px] text-stone-400" title="Data de criação da OF">
            Criada em {new Date(of.criado_em).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
          </span>
        </div>
      )}
    </Card>
  );
}
